import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DuplicateAction, DuplicateWarning, ExistingFileType } from '../../../models/bill-import.models';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { ExistingBillPdfService } from '../../../services/existing-bill-pdf.service';
import { formatBillPeriod } from '../../../utils/date-format.util';
import {
  existingFileTypes,
  existingPdfLinkLabel,
  formatMonthDay,
  isNearExactDuplicate,
  isRemittanceDuplicate,
  replaceBlockedMessage,
  replaceWarningText,
  sidesLabel,
  uploadBillSides,
} from '../state/duplicate-resolution.util';

interface PdfLink {
  type: ExistingFileType;
  label: string;
}

/** Everything one card renders, precomputed so the template calls no methods. */
interface DuplicateCard {
  billGroupId: string;
  existingBillId: number;
  heading: string;
  uploadDates: string;
  uploadTotals: string;
  uploadSides: string;
  /** Centre marker text between the two columns. */
  markerLabel: string;
  existingLabel: string;
  /** Second line under the label — only for a remittance, where the label can't carry the bill number. */
  existingBillLine: string | null;
  existingDates: string;
  existingTotals: string;
  existingSides: string;
  importedOn: string | null;
  pdfLinks: PdfLink[];
  selected: DuplicateAction;
  replaceable: boolean;
  skipLabel: string;
  replaceLabel: string;
  replaceWarning: string;
  blockedMessage: string | null;
}

const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

@Component({
  selector: 'app-duplicate-resolution-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './duplicate-resolution-modal.component.html',
  styleUrl: './duplicate-resolution-modal.component.scss'
})
export class DuplicateResolutionModalComponent {
  @Input() open = false;
  /** Moves keyboard focus into the dialog each time it opens. */
  @ViewChild('dialog') set dialog(el: ElementRef<HTMLElement> | undefined) {
    el?.nativeElement.focus();
  }
  @Output() openChange = new EventEmitter<boolean>();
  @Output() resolved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  readonly state = inject(BillAiFlowStore);
  private readonly pdfs = inject(ExistingBillPdfService);

  /** One card per exact duplicate, in match order; follows the store so choices stay in sync. */
  readonly cards = computed<DuplicateCard[]>(() =>
    this.state.duplicateDecisions().map(d => this.buildCard(d.billGroupId, d.duplicate, d.action)));

  readonly title = computed(() => {
    const n = this.cards().length;
    return n === 1 ? '1 bill is already imported' : `${n} bills are already imported`;
  });

  readonly subtitle = computed(() => {
    const decisions = this.state.duplicateDecisions();
    const match = decisions.some(d => d.duplicate.overlapType === 'NEAR_EXACT')
      ? 'Each one matches a bill already on that meter, with the same dates or a day apart.'
      : 'Each one has the same dates as a bill already on that meter.';
    const choice = decisions.some(d => d.duplicate.replaceable)
      ? 'Skip it, or replace the old bill with this one.'
      : "They'll be skipped.";
    return `${match} ${choice}`;
  });

  readonly summary = computed(() => {
    const cards = this.cards();
    const replaced = cards.filter(c => c.selected === 'REPLACE').length;
    const skipped = cards.length - replaced;
    const parts: string[] = [];
    if (replaced > 0) parts.push(replaced === 1 ? '1 replaces an existing bill' : `${replaced} replace existing bills`);
    if (skipped > 0) parts.push(`${skipped} skipped`);
    return parts.join(' · ');
  });

  selectAction(card: DuplicateCard, action: DuplicateAction): void {
    this.state.setDuplicateResolution(card.billGroupId, card.existingBillId, action);
  }

  openPdf(card: DuplicateCard, type: ExistingFileType): void {
    this.pdfs.open(this.state.getExistingBillRef(card.billGroupId), card.existingBillId, type);
  }

  confirm(): void {
    this.resolved.emit();
    this.close();
  }

  cancel(): void {
    this.cancelled.emit();
    this.close();
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  // Prevent clicks inside modal from closing it
  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  private buildCard(billGroupId: string, dup: DuplicateWarning, action: DuplicateAction): DuplicateCard {
    // Edited values from bill review win over the original extraction
    const data = this.state.billData()[billGroupId] ?? this.state.getBillFileForGroup(billGroupId)?.data;
    const unit = data?.usageUnit?.trim() || 'kWh';
    const id = dup.existingBillId;
    const remittance = isRemittanceDuplicate(dup);
    const files = existingFileTypes(dup);
    const created = formatMonthDay(dup.existingCreatedDate);

    // A $0 total on a usage-only bill (e.g. from a remittance) means no charges, not a $0 bill
    const hasCharges = dup.existingTotalCost != null && (dup.existingTotalCost !== 0 || (dup.existingSides?.length ?? 0) > 0);
    const existingCost = hasCharges ? MONEY.format(dup.existingTotalCost!) : 'No charges';
    const existingUsage = dup.existingTotalUsage != null ? `${NUMBER.format(dup.existingTotalUsage)} ${unit}` : null;

    return {
      billGroupId,
      existingBillId: id,
      heading: this.heading(billGroupId),
      uploadDates: formatBillPeriod(data?.billingPeriodStart, data?.billingPeriodEnd, 'Unknown dates'),
      uploadTotals: [
        MONEY.format(data?.totalAmount ?? 0),
        `${NUMBER.format(data?.usageAmount ?? 0)} ${unit}`,
      ].join(' · '),
      uploadSides: sidesLabel(uploadBillSides(data)),
      markerLabel: isNearExactDuplicate(dup) ? '1 day apart' : 'Same dates',
      existingLabel: remittance ? 'Already imported · from supplier remittance' : `Already imported · bill #${id}`,
      existingBillLine: remittance ? `Bill #${id}` : null,
      existingDates: formatBillPeriod(dup.existingStartDate, dup.existingEndDate, 'Unknown dates'),
      existingTotals: [existingCost, existingUsage].filter(Boolean).join(' · '),
      existingSides: sidesLabel(dup.existingSides),
      importedOn: created ? `Imported ${created}` : null,
      pdfLinks: files.map(type => ({ type, label: existingPdfLinkLabel(type, files.length, false) })),
      selected: action,
      replaceable: dup.replaceable === true,
      skipLabel: 'Skip this bill',
      replaceLabel: `Replace bill #${id}`,
      replaceWarning: replaceWarningText(dup),
      blockedMessage: replaceBlockedMessage(dup),
    };
  }

  /** "Meter number · service address" for the bill's meter. */
  private heading(billGroupId: string): string {
    const labels = this.state.getBillMeterLabels(billGroupId);
    return labels ? [labels.meter, labels.address].filter(Boolean).join(' · ') : 'Meter';
  }
}
